import React from "react";
import type { FunctionComponent, ReactNode } from "react";
import {
  EmailShareButton,
  FacebookShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import { useSdkContext } from "scmp-sdk/components/contexts";
import type { ShareMethod } from "scmp-sdk/lib/google-analytics/ga4-types";

import type { Props as CopyLinkProps } from "./copy-link-button";
import { CopyLinkButton } from "./copy-link-button";
import {
  CopyLinkIcon,
  EmailIcon,
  FacebookIcon,
  PrintIcon,
  TelegramIcon,
  TwitterIcon,
  WhatsappIcon,
} from "./icons";
import { PrintButton } from "./print-button";
import { Label, StyledIconContainer } from "./styles";
import type { IconProps } from "./types";

export type Props = {
  children?: ReactNode;
  className?: string;
  extraProps?: {
    copyLink?: Pick<CopyLinkProps, "disablePortal" | "tooltipPlacement" | "variant">;
    overrideIcons?: Partial<Record<SocialShareType, FunctionComponent<IconProps>>>;
  };
  hideIcon?: boolean;
  onClick?: () => void;
  position?: SharePositionType;
  relativeUrl: string;
  shortUrl?: null | string;
  showLabel?: boolean;
  style?: {
    backgroundColor?: string;
    color?: string;
    mobilePadding?: number;
    padding?: number;
    size?: number;
    svgTransform?: string;
  };
  title: string;
  type: SocialShareType;
  utm?: {
    campaign: string;
    content?: string;
    medium?: string;
  };
};

export type SharePositionType =
  | "article_adhesive"
  | "article_bottom"
  | "article_gallery"
  | "article_header"
  | "block_quote"
  | "history"
  | "live_snippet"
  | "series_bottom";

export type SocialShareType =
  | "copy-link"
  | "email"
  | "facebook"
  | "print"
  | "telegram"
  | "twitter"
  | "whatsapp";

export const ShareButton: React.FunctionComponent<Props> = ({
  children,
  className,
  extraProps,
  hideIcon = false,
  onClick,
  position,
  relativeUrl,
  shortUrl,
  showLabel = false,
  style,
  title,
  type,
  utm,
}) => {
  const { config, handleTrackGA4 } = useSdkContext();
  const absoluteUrl = new URL(shortUrl ?? relativeUrl, config.siteUrl);

  const renderIcon = () => {
    if (hideIcon) return null;

    const iconMaps: Record<SocialShareType, FunctionComponent<IconProps>> = {
      "copy-link": CopyLinkIcon,
      email: EmailIcon,
      facebook: FacebookIcon,
      print: PrintIcon,
      telegram: TelegramIcon,
      twitter: TwitterIcon,
      whatsapp: WhatsappIcon,
      ...extraProps?.overrideIcons,
    };

    const Icon = iconMaps[type];

    return (
      <StyledIconContainer
        $backgroundColor={style?.backgroundColor ?? "transparent"}
        $iconFillColor={style?.color}
        $mobilePadding={style?.mobilePadding ?? 0}
        $padding={style?.padding ?? 0}
        $size={style?.size ?? 36}
        $svgTransform={style?.svgTransform}
      >
        <Icon />
      </StyledIconContainer>
    );
  };
  const renderLabel = (label: string) => showLabel && <Label>{label}</Label>;

  const getTypeShareMethod = (): ShareMethod => {
    switch (type) {
      case "copy-link":
        return "copy_link";
      default:
        return type;
    }
  };

  const handleTrackGA4Event = () => {
    const method: ShareMethod = getTypeShareMethod();
    const shareUrl = new URL(relativeUrl, config.siteUrl).href;

    if (position) {
      handleTrackGA4({
        action: "click",
        category: "share",
        customized_parameters: {
          method,
          position,
          share_url: shareUrl,
        },
        subcategory: "",
      });
    }
  };

  const handleClick = () => {
    handleTrackGA4Event();
    onClick?.();
  };

  const computeAugmentedUrlWithUTMParameters = () => {
    if (!utm?.campaign) return absoluteUrl.href;
    const augmentedUrl = new URL(absoluteUrl);
    augmentedUrl.searchParams.set("utm_source", type);
    augmentedUrl.searchParams.set("utm_campaign", utm.campaign);
    if (utm.medium) augmentedUrl.searchParams.set("utm_medium", utm.medium);
    if (utm.content) augmentedUrl.searchParams.set("utm_content", utm.content);
    return augmentedUrl.href;
  };
  const augmentedUrl = computeAugmentedUrlWithUTMParameters();

  const getNewsletterMessage = (link: string) =>
    `Get the latest news, insights and special reports delivered straight to your inbox. Sign up to our newsletters: ${link}`;

  switch (type) {
    case "copy-link":
      return (
        <CopyLinkButton
          className={className}
          disablePortal={extraProps?.copyLink?.disablePortal}
          LabelComponent={Label}
          onClick={handleClick}
          showLabel={showLabel}
          tooltipPlacement={extraProps?.copyLink?.tooltipPlacement ?? "bottom-start"}
          url={augmentedUrl}
          variant={extraProps?.copyLink?.variant ?? "tooltip"}
        >
          {renderIcon()}
          {children}
        </CopyLinkButton>
      );
    case "email": {
      const bodyMessage =
        `${title}\r\n` +
        `${augmentedUrl}\r\n` +
        `------------------------------\r\n` +
        getNewsletterMessage("https://sc.mp/newsletter_email");
      return (
        <EmailShareButton
          beforeOnClick={handleClick}
          body={bodyMessage}
          className={className}
          subject="Shared from South China Morning Post"
          url=""
        >
          {renderIcon()}
          {renderLabel("Email")}
          {children}
        </EmailShareButton>
      );
    }
    case "facebook":
      return (
        <FacebookShareButton className={className} onClick={handleClick} url={augmentedUrl}>
          {renderIcon()}
          {renderLabel("Facebook")}
          {children}
        </FacebookShareButton>
      );
    case "print":
      return (
        <PrintButton className={className} onClick={handleClick} url={relativeUrl}>
          {renderIcon()}
          {renderLabel("Print")}
          {children}
        </PrintButton>
      );
    case "telegram": {
      const bodyMessage =
        `${title}\n` +
        `${augmentedUrl}\n` +
        "------------------------------\n" +
        "Join SCMP's Channel: t.me/scmp_news - Get Daily News Update\n" +
        getNewsletterMessage("https://sc.mp/newsletter_telegram");
      return (
        <TelegramShareButton
          className={className}
          onClick={handleClick}
          title={bodyMessage}
          url=" "
        >
          {renderIcon()}
          {renderLabel("Telegram")}
          {children}
        </TelegramShareButton>
      );
    }
    case "twitter":
      return (
        <TwitterShareButton
          className={className}
          onClick={handleClick}
          title={`${title}\n`}
          url={augmentedUrl}
          via={config.twitter.accountName.toLowerCase()}
        >
          {renderIcon()}
          {renderLabel("X/Twitter")}
          {children}
        </TwitterShareButton>
      );
    case "whatsapp": {
      const bodyMessage =
        `${title}\n` +
        `${augmentedUrl}\n` +
        "------------------------------\n" +
        getNewsletterMessage("https://sc.mp/newsletter_whatsapp");
      return (
        <WhatsappShareButton
          className={className}
          onClick={handleClick}
          title={bodyMessage}
          url=" "
        >
          {renderIcon()}
          {renderLabel("Whatsapp")}
          {children}
        </WhatsappShareButton>
      );
    }
    default:
      return null;
  }
};

ShareButton.displayName = "ShareButton";
